import { useState } from 'react';

// --- Icons (Inline SVGs for Lucide-style icons) ---
const CheckIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>
);

const TrashIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path></svg>
);

const PlusIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line></svg>
);

const LayoutIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect><line x1="3" y1="9" x2="21" y2="9"></line><line x1="9" y1="21" x2="9" y2="9"></line></svg>
);

export default function App() {
  const [tasks, setTasks] = useState([
    { id: 1, text: "Design the landing page hero section", completed: false, priority: "High" },
    { id: 2, text: "Review pull requests for the API integration", completed: true, priority: "Medium" },
    { id: 3, text: "Update project documentation", completed: false, priority: "Low" }
  ]);
  const [inputValue, setInputValue] = useState("");
  const [priority, setPriority] = useState("Medium");

  const addTask = (e) => {
    e.preventDefault();
    if (!inputValue.trim()) return;
    
    const newTask = {
      id: Date.now(),
      text: inputValue,
      completed: false,
      priority: priority
    };
    
    setTasks([newTask, ...tasks]);
    setInputValue("");
  };

  const toggleTask = (id) => {
    setTasks(tasks.map(task => 
      task.id === id ? { ...task, completed: !task.completed } : task
    ));
  };

  const deleteTask = (id) => {
    setTasks(tasks.filter(task => task.id !== id));
  };

  const completedCount = tasks.filter(t => t.completed).length;
  const completionRate = tasks.length > 0 ? Math.round((completedCount / tasks.length) * 100) : 0;

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 font-sans p-4 md:p-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <header className="flex flex-col md:flex-row md:items-center justify-between mb-8 gap-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight text-indigo-600 flex items-center gap-2">
              <LayoutIcon /> FocusFlow
            </h1>
            <p className="text-slate-500 mt-1">Keep track of your projects and daily tasks.</p>
          </div>
          <div className="bg-white p-4 rounded-2xl shadow-sm border border-slate-200 flex gap-6">
            <div className="text-center">
              <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Total Tasks</p>
              <p className="text-2xl font-bold">{tasks.length}</p>
            </div>
            <div className="w-px bg-slate-200" />
            <div className="text-center">
              <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Completion</p>
              <p className="text-2xl font-bold text-green-600">{completionRate}%</p>
            </div>
          </div>
        </header>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {/* Main Content: Task List */}
          <div className="md:col-span-2 space-y-6">
            {/* Input Form */}
            <form onSubmit={addTask} className="bg-white p-4 rounded-2xl shadow-sm border border-slate-200 flex flex-col sm:flex-row gap-3">
              <input
                type="text"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                placeholder="What needs to be done?"
                className="flex-1 bg-transparent border-none focus:ring-0 text-lg"
              />
              <div className="flex items-center gap-2">
                <select 
                  value={priority}
                  onChange={(e) => setPriority(e.target.value)}
                  className="bg-slate-100 border-none rounded-lg text-sm px-3 py-2 focus:ring-2 focus:ring-indigo-500"
                >
                  <option>High</option>
                  <option>Medium</option>
                  <option>Low</option>
                </select>
                <button 
                  type="submit"
                  className="bg-indigo-600 hover:bg-indigo-700 text-white p-2 rounded-lg transition-colors flex items-center justify-center w-10 h-10"
                >
                  <PlusIcon />
                </button>
              </div>
            </form>

            {/* Task List */}
            <div className="space-y-3">
              {tasks.length === 0 ? (
                <div className="text-center py-12 bg-white rounded-2xl border border-dashed border-slate-300">
                  <p className="text-slate-400">No tasks yet. Start by adding one above!</p>
                </div>
              ) : (
                tasks.map(task => (
                  <div 
                    key={task.id}
                    className={`group flex items-center justify-between p-4 rounded-2xl bg-white border transition-all duration-200 ${
                      task.completed ? 'opacity-60 border-slate-100' : 'border-slate-200 shadow-sm hover:shadow-md'
                    }`}
                  >
                    <div className="flex items-center gap-4">
                      <button 
                        onClick={() => toggleTask(task.id)}
                        className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-colors ${
                          task.completed 
                            ? 'bg-green-500 border-green-500 text-white' 
                            : 'border-slate-300 hover:border-indigo-500'
                        }`}
                      >
                        {task.completed && <CheckIcon />}
                      </button>
                      <div>
                        <p className={`font-medium ${task.completed ? 'line-through text-slate-400' : 'text-slate-700'}`}>
                          {task.text}
                        </p>
                        <span className={`text-[10px] uppercase font-bold tracking-widest px-2 py-0.5 rounded-full ${
                          task.priority === 'High' ? 'bg-red-100 text-red-600' :
                          task.priority === 'Medium' ? 'bg-amber-100 text-amber-600' :
                          'bg-blue-100 text-blue-600'
                        }`}>
                          {task.priority}
                        </span>
                      </div>
                    </div>
                    <button 
                      onClick={() => deleteTask(task.id)}
                      className="text-slate-300 hover:text-red-500 p-2 opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <TrashIcon />
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Sidebar: Stats & Info */}
          <div className="space-y-6">
            <div className="bg-indigo-900 text-indigo-50 p-6 rounded-2xl shadow-xl">
              <h3 className="text-lg font-semibold mb-2">Daily Goal</h3>
              <div className="w-full bg-indigo-800 rounded-full h-2 mb-4">
                <div 
                  className="bg-indigo-400 h-2 rounded-full transition-all duration-500" 
                  style={{ width: `${completionRate}%` }}
                ></div>
              </div>
              <p className="text-sm text-indigo-200 leading-relaxed">
                You've completed {completedCount} tasks today. Focus on your high-priority items to maximize impact.
              </p>
            </div>

            <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm">
              <h3 className="text-slate-900 font-semibold mb-4">Focus Tips</h3>
              <ul className="space-y-4 text-sm text-slate-600">
                <li className="flex gap-3">
                  <span className="w-5 h-5 rounded bg-indigo-50 text-indigo-600 flex items-center justify-center font-bold text-[10px]">1</span>
                  <span>Use the 2-minute rule for small tasks.</span>
                </li>
                <li className="flex gap-3">
                  <span className="w-5 h-5 rounded bg-indigo-50 text-indigo-600 flex items-center justify-center font-bold text-[10px]">2</span>
                  <span>Prioritize deep work in the morning.</span>
                </li>
                <li className="flex gap-3">
                  <span className="w-5 h-5 rounded bg-indigo-50 text-indigo-600 flex items-center justify-center font-bold text-[10px]">3</span>
                  <span>Review your list every evening.</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}